/**
 * Pieces shared by the Traffic sources hub and a source's own page:
 * icons, plain-word labels, the little menus (status, owner, kind) and the
 * team list for owner pickers.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  BadgeDollarSign, BadgePercent, Building2, Check, ChevronDown, CircleDashed, Clapperboard, Globe, Handshake,
  Lightbulb, Mailbox, MapPin, Megaphone, MessagesSquare, Mic, Music2, Newspaper, Repeat, Search, Send,
  Sparkles, Store, Target, Users,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";
import { Pill, STATUS_COLOR, STATUS_LABEL, firstName, softClass } from "@/features/command/ui";
import type { SourceStatus, TeamMember } from "./types";

// ─── Icons ───────────────────────────────────────────────────────────────────

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  Sparkles, Megaphone, Search, Music2, Repeat, BadgeDollarSign, BadgePercent, Store, Handshake, Lightbulb, Send,
  Clapperboard, MessagesSquare, Globe, Mic, Newspaper, MapPin, Mailbox, Building2, CircleDashed, Users, Target,
  // Brand icons are gone from lucide; a megaphone reads fine for an ad platform.
  Facebook: Megaphone,
};

const KIND_ICON: Record<string, ComponentType<{ className?: string }>> = {
  organic: Sparkles, paid: Megaphone, affiliate: BadgeDollarSign, partner: Handshake, outreach: Send, ugc: Clapperboard,
  community: MessagesSquare, seo: Globe, podcast: Mic, media: Newspaper, offline: MapPin, guru: Lightbulb, list: Mailbox,
  direct: CircleDashed, other: Target,
};

export function sourceIcon(icon: string | null | undefined, kind?: string | null): ComponentType<{ className?: string }> {
  return (icon && ICONS[icon]) || KIND_ICON[String(kind ?? "")] || Target;
}

/** The coloured rounded square with the source's icon. */
export function SourceGlyph({ icon, kind, color, size = "md", className }: {
  icon: string | null | undefined; kind?: string | null; color: string | null | undefined; size?: "sm" | "md" | "lg"; className?: string;
}) {
  const Icon = sourceIcon(icon, kind);
  return (
    <span className={cn(
      "inline-flex shrink-0 items-center justify-center ring-1 ring-inset",
      softClass(color),
      size === "sm" ? "h-6 w-6 rounded-lg" : size === "lg" ? "h-12 w-12 rounded-2xl" : "h-9 w-9 rounded-xl",
      className,
    )}>
      <Icon className={size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-6 w-6" : "h-4.5 w-4.5"} />
    </span>
  );
}

// ─── Words ───────────────────────────────────────────────────────────────────

export const KINDS: Array<{ value: string; label: string }> = [
  { value: "organic", label: "Organic social" },
  { value: "paid", label: "Paid ads" },
  { value: "affiliate", label: "Affiliates" },
  { value: "partner", label: "Partners" },
  { value: "outreach", label: "Outreach" },
  { value: "ugc", label: "Creators / UGC" },
  { value: "community", label: "Communities" },
  { value: "seo", label: "Search + AI answers" },
  { value: "podcast", label: "Podcasts" },
  { value: "media", label: "Media / PR" },
  { value: "offline", label: "Offline" },
  { value: "guru", label: "Gurus" },
  { value: "list", label: "Email list" },
  { value: "direct", label: "Direct" },
  { value: "other", label: "Other" },
];
export const kindLabel = (k: string | null | undefined) => KINDS.find((x) => x.value === k)?.label ?? "Other";

export const STATUSES: SourceStatus[] = ["active", "testing", "idea", "paused"];
const STATUS_HINT: Record<SourceStatus, string> = {
  active: "Running and worth the time",
  testing: "Trying it out",
  idea: "On the plan, not started",
  paused: "On hold for now",
};

export const AFFILIATE_KIND: Record<string, string> = {
  partner: "JV partner", pro: "Pro affiliate", creator: "Content creator", casual: "Casual sharer",
};
export const AFFILIATE_CHANNEL: Record<string, string> = { syncsuite: "Our program", clickbank: "ClickBank", other: "Somewhere else" };

export const PLATFORM_LABEL: Record<string, string> = {
  facebook: "Meta", instagram: "Instagram", google: "Google", tiktok: "TikTok", youtube: "YouTube", linkedin: "LinkedIn",
  twitter: "X", x: "X", pinterest: "Pinterest", reddit: "Reddit", threads: "Threads", bluesky: "Bluesky",
};
export const platformLabel = (p: string | null | undefined) => {
  const k = String(p ?? "").toLowerCase();
  return PLATFORM_LABEL[k] ?? (k ? k.charAt(0).toUpperCase() + k.slice(1) : "Other");
};

const CLICK_ID_LABEL: Record<string, string> = {
  gclid: "Google", gbraid: "Google (iPhone)", wbraid: "Google (iPhone web)", ttclid: "TikTok", msclkid: "Microsoft / Bing", fbclid: "Facebook or Instagram",
};
export const clickIdLabel = (k: string) => CLICK_ID_LABEL[k] ?? k;

export const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** "Clicked a Google ad" / "Referred by Jana" / "Came from chatgpt.com". */
export function howLabel(p: { credit_model: string | null; last_how: string | null; affiliate_name: string | null }): string {
  if (p.affiliate_name) return `Referral code from ${p.affiliate_name}`;
  const how = (p.last_how ?? "").trim();
  const ad = /^ad click \(([^)]+)\)$/.exec(how);
  if (ad) {
    const ids = ad[1]!.split(/,\s*/);
    if (ids.every((x) => x === "fbclid")) return "Clicked a link on Facebook or Instagram";
    return `Clicked a ${[...new Set(ids.filter((x) => x !== "fbclid").map(clickIdLabel))].join(" / ")} ad`;
  }
  const tagged = /^link tagged (.+)$/.exec(how);
  if (tagged) return `Tagged link: ${tagged[1]}`;
  if (how) return sentence(how);
  return {
    manual: "Set on purpose",
    affiliate: "Signed up with a referral code",
    last_touch: "Last place before signing up",
    first_touch: "First place they came from",
    platform: "How the platform says they signed up",
    direct: "No source recorded",
  }[p.credit_model ?? ""] ?? "No source recorded";
}

export function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try { return new URL(url.includes("://") ? url : `https://${url}`).hostname.replace(/^www\./, ""); } catch { return null; }
}

export function errMsg(e: unknown, fallback = "Something went wrong"): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

const realPart = (v: string | null | undefined) => (v && !/^(undefined|null)$/i.test(v.trim()) ? v.trim() : "");
export const personName = (p: { first_name: string | null; last_name: string | null; email?: string | null }) =>
  [realPart(p.first_name), realPart(p.last_name)].filter(Boolean).join(" ") || p.email || "Someone";

// ─── Team (owner pickers) ────────────────────────────────────────────────────

let teamCache: Promise<TeamMember[]> | null = null;
export function useTeam(): TeamMember[] {
  const [members, setMembers] = useState<TeamMember[]>([]);
  useEffect(() => {
    let live = true;
    teamCache ??= api.get<{ members: TeamMember[] }>("/team").then((r) => r.members ?? []).catch(() => { teamCache = null; return []; });
    void teamCache.then((m) => { if (live) setMembers(m); });
    return () => { live = false; };
  }, []);
  return members;
}

// ─── A small floating menu (portal, so glass cards never clip it) ───────────

export function Menu({
  trigger, children, align = "left", width = 220, className,
}: {
  trigger: (p: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  width?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Placed under the trigger (or above it when there's no room), and kept
  // there while the page scrolls.
  const place = useCallback(() => {
    if (!anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = align === "right" ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const below = r.bottom + 6;
    const h = panel.current?.offsetHeight ?? 200;
    const top = below + h > window.innerHeight - 8 && r.top - h - 6 > 8 ? r.top - h - 6 : below;
    setPos({ top, left });
  }, [align, width]);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <>
      <span ref={anchor} className={cn("inline-flex", className)}>{trigger({ open, toggle: () => setOpen((o) => !o) })}</span>
      {open && createPortal(
        <div
          ref={panel}
          style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
          className="z-[70] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 p-1 shadow-2xl shadow-shade/40 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {children(() => setOpen(false))}
        </div>,
        document.body,
      )}
    </>
  );
}

export function MenuItem({ children, onClick, active, danger, icon: Icon, hint }: {
  children: ReactNode; onClick: () => void; active?: boolean; danger?: boolean; icon?: ComponentType<{ className?: string }>; hint?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors",
        danger ? "text-rose-300 hover:bg-rose-500/10" : "text-zinc-200 hover:bg-zinc-800/70",
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-zinc-400" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {hint && <span className="block truncate text-[11px] text-zinc-500">{hint}</span>}
      </span>
      {active && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-300" />}
    </button>
  );
}

// ─── Status / owner / kind pickers ───────────────────────────────────────────

export function StatusPill({ status, className, children }: { status: string; className?: string; children?: ReactNode }) {
  return (
    <Pill color={STATUS_COLOR[status] ?? "zinc"} className={className}>
      <span className={cn("h-1.5 w-1.5 rounded-full", status === "active" ? "bg-emerald-400" : status === "testing" ? "bg-amber-400" : status === "paused" ? "bg-slate-400" : "bg-zinc-400")} />
      {STATUS_LABEL[status] ?? status}
      {children}
    </Pill>
  );
}

export function StatusMenu({ status, onChange, className }: { status: string; onChange: (s: SourceStatus) => void; className?: string }) {
  return (
    <Menu
      width={230}
      className={className}
      trigger={({ toggle }) => (
        <button
          type="button"
          title="Change status"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(); }}
          className="group/st inline-flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
        >
          <StatusPill status={status} className="pr-1.5 transition-[filter] group-hover/st:brightness-125">
            <ChevronDown className="-mr-0.5 h-3 w-3 opacity-50 transition-opacity group-hover/st:opacity-100" />
          </StatusPill>
        </button>
      )}
    >
      {(close) => STATUSES.map((s) => (
        <MenuItem key={s} active={s === status} hint={STATUS_HINT[s]} onClick={() => { close(); if (s !== status) onChange(s); }}>
          <StatusPill status={s} />
        </MenuItem>
      ))}
    </Menu>
  );
}

export function OwnerMenu({ ownerId, ownerName, onChange, team, compact }: {
  ownerId: string | null; ownerName: string | null; onChange: (id: string | null, name: string | null) => void; team: TeamMember[]; compact?: boolean;
}) {
  return (
    <Menu
      width={220}
      trigger={({ toggle }) => (
        <button
          type="button"
          title="Change owner"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(); }}
          className="inline-flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 text-xs text-zinc-300 ring-1 ring-inset ring-zinc-800 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100"
        >
          {ownerName ? <Avatar name={ownerName} size="xs" className="h-5 w-5 text-[9px]" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-zinc-600 text-zinc-500"><Users className="h-3 w-3" /></span>}
          {ownerName ? (compact ? firstName(ownerName) : ownerName) : "No owner"}
        </button>
      )}
    >
      {(close) => (
        <>
          {team.map((m) => (
            <MenuItem key={m.id} active={m.id === ownerId} onClick={() => { close(); if (m.id !== ownerId) onChange(m.id, m.name); }}>
              <span className="flex items-center gap-2"><Avatar name={m.name} email={m.email} size="xs" />{m.name || m.email}</span>
            </MenuItem>
          ))}
          {!team.length && <div className="px-2.5 py-2 text-xs text-zinc-500">Loading the team…</div>}
          <div className="my-1 border-t border-zinc-800" />
          <MenuItem active={!ownerId} onClick={() => { close(); if (ownerId) onChange(null, null); }}>No owner</MenuItem>
        </>
      )}
    </Menu>
  );
}

export function KindMenu({ kind, onChange }: { kind: string; onChange: (k: string) => void }) {
  return (
    <Menu
      width={210}
      trigger={({ toggle }) => (
        <button
          type="button"
          title="Change what kind of source this is"
          onClick={(e) => { e.preventDefault(); toggle(); }}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-zinc-400 ring-1 ring-inset ring-zinc-800 transition-colors hover:bg-zinc-800/60 hover:text-zinc-100"
        >
          {kindLabel(kind)}
          <ChevronDown className="h-3 w-3" />
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-72 overflow-y-auto">
          {KINDS.map((k) => (
            <MenuItem key={k.value} icon={KIND_ICON[k.value]} active={k.value === kind} onClick={() => { close(); if (k.value !== kind) onChange(k.value); }}>
              {k.label}
            </MenuItem>
          ))}
        </div>
      )}
    </Menu>
  );
}
